import { useCallback, useMemo } from "react"
import { useTranslation } from "react-i18next"
import {
  useRunActionQuery,
  useAccelActionQuery,
  useDecelActionQuery,
} from "@/hooks/api/useSettingQuery"
import { useMetricList } from "@/hooks/api/useMetricQuery"
import { getMetricTranslateKey } from "@/utils/metric"
import { MetricMeta } from "@/types/metric"

/**
 * 내부 공통 로직 훅
 */
function useTranslateMetricCore() {
  const { t, i18n } = useTranslation()
  const { data: run } = useRunActionQuery({ enabled: true })
  const { data: accel } = useAccelActionQuery({ enabled: true })
  const { data: decel } = useDecelActionQuery({ enabled: true })
  const { data: metricListData } = useMetricList()

  // O(1) lookup을 위한 metricMap
  const metricMap = useMemo(() => {
    if (!metricListData) return new Map<string, MetricMeta>()
    return metricListData.reduce((map, metric) => {
      map.set(metric.key, metric)
      return map
    }, new Map<string, MetricMeta>())
  }, [metricListData])

  // 기존 i18n 번역 로직 (내부용)
  const translateKeyByI18n = useCallback(
    (key: string) => {
      if (!key) return ""

      const hasEffortPattern = key.includes("Effort") || /acr[RAD]\d/.test(key)
      if (hasEffortPattern && run && accel && decel) {
        let newKey = key

        if (key.includes("metric.")) {
          newKey = key.split("metric.")[1]
        }

        return getMetricTranslateKey(newKey, t, { run, accel, decel })
      }

      if (key.includes("metric.") || !i18n.exists(`metric.${key}`)) {
        return t(key) // fallback
      }

      return t("metric." + key)
    },
    [run, accel, decel, t, i18n],
  )

  // key만 받는 버전 - metricMap에서 조회 후 없으면 i18n 번역
  const translateKey = useCallback(
    (key: string) => {
      // "metric." prefix가 있으면 제거하고 조회
      const pureKey = key.includes("metric.") ? key.split("metric.")[1] : key
      const metric = metricMap.get(pureKey)

      // calculated metric이면 name 반환
      if (metric?.name) {
        return metric.name
      }

      // 기존 i18n 번역 로직
      return translateKeyByI18n(key)
    },
    [metricMap, translateKeyByI18n],
  )

  // MetricMeta 객체를 받는 버전 - 더 효율적 (조회 스킵)
  const translateMetric = useCallback(
    (metric: MetricMeta) => {
      // name이 있으면 (calculated metric) 바로 반환
      if (metric.name) {
        return metric.name
      }

      // 없으면 기존 i18n 번역 로직
      return translateKeyByI18n(metric.key)
    },
    [translateKeyByI18n],
  )

  return { translateKey, translateMetric }
}

/**
 * key(string)를 받아서 번역된 메트릭 이름 반환
 * - calculated metric이면 name 필드 사용
 * - 일반 metric이면 i18n 번역
 */
export function useTranslateMetricKey() {
  const { translateKey } = useTranslateMetricCore()
  return translateKey
}

/**
 * MetricMeta 객체를 받아서 번역된 메트릭 이름 반환
 * - 조회 없이 바로 처리하므로 더 효율적
 */
export function useTranslateMetric() {
  const { translateMetric } = useTranslateMetricCore()
  return translateMetric
}
